import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageHeader from "../karkhana/ui/PageHeader";
import notifications from "../../common/Components/Notifications/Notification";
import {
  OrderCard,
  SharePolicy,
  acceptTerms,
  declineOrder,
  incomingOrders,
  incomingPartnershipAction,
  incomingPartnerships,
  inr,
  saveSharePolicy,
  shortDate,
} from "../../services/partnerNetworkApi";
import { OrderCardView } from "./ProductionBoard";
import { STATUS_LABEL } from "./pnUi";
import "../karkhana/ui/karkhana-ui.scss";
import "./partner-network.scss";

const CATEGORY_LABEL: Record<string, string> = {
  STATUS: "Stage and status",
  PHOTOS: "Photos",
  QUANTITY_PROGRESS: "Pieces done",
  WEAVER_NAMES: "Weaver names",
  LOOM_LOCATION: "Loom location",
  COSTS: "Costs",
  DOCUMENTS: "Documents",
};

const RULES: { id: "AUTO" | "ASK" | "NEVER"; label: string }[] = [
  { id: "AUTO", label: "Always" },
  { id: "ASK", label: "Ask me" },
  { id: "NEVER", label: "Never" },
];

/** Partner side (any studio, free): orders from designers, invitations, and what each designer may see. */
const IncomingOrdersPage = () => {
  const location = useLocation();
  const history = useHistory();
  const [tab, setTab] = useState(new URLSearchParams(location.search).get("tab") === "partners" ? "partners" : "orders");
  const [orders, setOrders] = useState<OrderCard[]>([]);
  const [partnerships, setPartnerships] = useState<any[]>([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    incomingOrders().then((res) => res.success && setOrders(res.data));
    incomingPartnerships().then((res) => res.success && setPartnerships(res.data));
  };

  useEffect(load, []);

  const waiting = orders.filter((o) => (o.status === "SENT" || o.status === "COUNTERED") && o.turn === "PARTNER");
  const rest = orders.filter((o) => !waiting.includes(o));
  const invites = partnerships.filter((p) => p.status === "INVITED");

  const act = async (key: string, call: () => Promise<any>, done: string) => {
    setBusy(key);
    const res = await call();
    setBusy("");
    notifications({ title: "Orders", message: res.success ? done : res.message, type: res.success ? "success" : "danger" });
    load();
  };

  return (
    <div className="pn-page">
      <PageHeader title="Incoming orders" />
      <div className="pn-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "orders"} className={tab === "orders" ? "is-on" : ""} onClick={() => setTab("orders")}>
          Orders {waiting.length ? `(${waiting.length})` : ""}
        </button>
        <button role="tab" aria-selected={tab === "partners"} className={tab === "partners" ? "is-on" : ""} onClick={() => setTab("partners")}>
          Designers {invites.length ? `(${invites.length})` : ""}
        </button>
      </div>

      {tab === "orders" ? (
        <>
          {invites.map((p) => (
            <div className="pn-card" key={p.id}>
              <b>{p.designer}</b> wants to send you production orders. Nothing in your studio becomes visible when you accept.
              <div className="pn-actions">
                <button className="kh-btn is-primary" disabled={!!busy} onClick={() => act(p.id, () => incomingPartnershipAction(p.id, "accept"), "Accepted")}>Accept</button>
                <button className="kh-btn" disabled={!!busy} onClick={() => act(p.id, () => incomingPartnershipAction(p.id, "decline"), "Declined")}>Decline</button>
              </div>
            </div>
          ))}
          {waiting.length ? (
            <div className="pn-card">
              <h3>Waiting for you</h3>
              <ul className="pn-list">
                {waiting.map((o) => (
                  <li key={o.id}>
                    <div>
                      <div className="pn-list__name">{o.code} · {o.title}</div>
                      <div className="pn-list__meta">
                        <span>{o.designer}</span>
                        <span className={`pn-chip${o.status === "COUNTERED" ? " is-warn" : ""}`}>{STATUS_LABEL[o.status]}</span>
                        <span>{o.quantity ?? "—"} pcs · {inr(o.rate)} / pc · due {shortDate(o.dueDate)}</span>
                      </div>
                    </div>
                    <div className="pn-list__side">
                      <button className="kh-btn is-primary" disabled={!!busy} onClick={() => act(o.id, () => acceptTerms(o.id), "Order accepted")}>Accept</button>
                      <button className="kh-btn" onClick={() => history.push(`/partner-network/incoming/${o.id}`)}>Counter</button>
                      <button className="kh-btn" disabled={!!busy} onClick={() => window.confirm("Decline this order?") && act(o.id, () => declineOrder(o.id), "Declined")}>Decline</button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="pn-board" style={{ gridAutoFlow: "row", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gridAutoColumns: "auto" }}>
            {rest.map((o) => (
              <OrderCardView key={o.id} o={o} to={`/partner-network/incoming/${o.id}`} showPartner={false} />
            ))}
          </div>
          {!orders.length && !invites.length ? <div className="pn-card"><p className="pn-muted">No orders yet. When a designer sends you one it appears here.</p></div> : null}
        </>
      ) : (
        partnerships.map((p) => <PolicyCard key={p.id} p={p} onChanged={load} />)
      )}
    </div>
  );
};

const PolicyCard = ({ p, onChanged }: { p: any; onChanged: () => void }) => {
  const [policy, setPolicy] = useState<SharePolicy>(p.sharePolicy || {});
  const [saving, setSaving] = useState(false);
  const ended = p.status === "ENDED";
  return (
    <div className="pn-card">
      <h3>{p.designer} <span className="pn-chip">{STATUS_LABEL[p.status]}</span></h3>
      <p className="pn-muted">What {p.designer} may see on your orders. Always = shared as you add it; Ask me = off until you turn it on; Never = stays internal.</p>
      <div className="pn-policy">
        {Object.keys(CATEGORY_LABEL).map((cat) => (
          <div key={cat}>
            <span>{CATEGORY_LABEL[cat]}</span>
            <div className="pn-seg" role="radiogroup" aria-label={CATEGORY_LABEL[cat]}>
              {RULES.map((r) => (
                <button key={r.id} role="radio" aria-checked={policy[cat] === r.id} className={policy[cat] === r.id ? "is-on" : ""}
                  disabled={ended} onClick={() => setPolicy({ ...policy, [cat]: r.id })}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!ended ? (
        <div className="pn-actions">
          <button className="kh-btn is-primary" disabled={saving} onClick={async () => {
            setSaving(true);
            const res = await saveSharePolicy(p.id, policy);
            setSaving(false);
            notifications({ title: p.designer, message: res.success ? "Share settings saved" : res.message, type: res.success ? "success" : "danger" });
          }}>Save</button>
          {p.status !== "INVITED" ? (
            <button className="kh-btn" onClick={async () => {
              if (!window.confirm(`End working with ${p.designer}? No new orders; shared history stays read-only; all data stays with its owner.`)) return;
              await incomingPartnershipAction(p.id, "end");
              onChanged();
            }}>End partnership</button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default IncomingOrdersPage;
